import { useState } from "react";
import { documentTypes } from "@/mocks/documentos";

interface NewDocumentModalProps {
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => void;
}

export default function NewDocumentModal({ onClose, onSubmit }: NewDocumentModalProps) {
  const [formData, setFormData] = useState({
    type: "",
    title: "",
    number: "",
    description: "",
    project: "",
    municipality: "",
    uf: "",
    primaryBinding: "",
    ctaCode: "",
    areaId: "",
    issuingBody: "",
    responsible: "",
    issueDate: "",
    expiryDate: "",
    status: "Rascunho",
    tags: "",
    observations: "",
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 shadow-xl overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading">Novo Documento</h2>
              <p className="text-xs text-foreground-500 mt-1">Cadastre um novo documento ou fonte documental</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors" aria-label="Fechar">
              <i className="ri-close-line w-full h-full flex items-center justify-center text-lg text-foreground-500" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Tipo de documento *</label>
              <select
                value={formData.type}
                onChange={(e) => handleChange("type", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
              >
                <option value="">Selecione o tipo...</option>
                {documentTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Título *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Título do documento"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Número/Código</label>
                <input
                  type="text"
                  value={formData.number}
                  onChange={(e) => handleChange("number", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Ex: DOC-2026-001"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Status inicial</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                >
                  <option value="Rascunho">Rascunho</option>
                  <option value="Em revisão">Em revisão</option>
                  <option value="Validado">Validado</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                placeholder="Descrição do documento..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Projeto</label>
                <select
                  value={formData.project}
                  onChange={(e) => handleChange("project", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                >
                  <option value="">Selecione...</option>
                  <option value="prj-001">Inventário Arbóreo Goiânia Norte</option>
                  <option value="prj-002">Arborização Loteamento Jardim Verde</option>
                  <option value="prj-003">Compensação Reserva Buritis</option>
                  <option value="prj-004">Reflorestamento APP Córrego Matinha</option>
                  <option value="prj-006">Parque Municipal Buriti Sereno</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Município/UF</label>
                <input
                  type="text"
                  value={formData.municipality}
                  onChange={(e) => handleChange("municipality", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Ex: Goiânia/GO"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Vínculo principal</label>
              <input
                type="text"
                value={formData.primaryBinding}
                onChange={(e) => handleChange("primaryBinding", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Projeto, árvore CTA, área verde ou UC"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Vincular a CTA</label>
                <input
                  type="text"
                  value={formData.ctaCode}
                  onChange={(e) => handleChange("ctaCode", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Ex: CTA-GO-001"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Vincular a Área/UC</label>
                <input
                  type="text"
                  value={formData.areaId}
                  onChange={(e) => handleChange("areaId", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Nome da área ou UC"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Órgão emissor</label>
                <input
                  type="text"
                  value={formData.issuingBody}
                  onChange={(e) => handleChange("issuingBody", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Órgão ou entidade"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Responsável técnico</label>
                <input
                  type="text"
                  value={formData.responsible}
                  onChange={(e) => handleChange("responsible", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Nome do responsável"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Data de emissão</label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => handleChange("issueDate", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1">Data de validade</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => handleChange("expiryDate", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Tags</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => handleChange("tags", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Separe por vírgulas"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Observações</label>
              <textarea
                value={formData.observations}
                onChange={(e) => handleChange("observations", e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                placeholder="Observações adicionais..."
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-4 border-t border-background-200">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
              Cancelar
            </button>
            <button onClick={handleSubmit} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-save-line w-4 h-4 inline-flex items-center justify-center mr-1" />
              Salvar documento
            </button>
          </div>
        </div>
      </div>
    </>
  );
}